/**
 * Contêiner GLB (glTF 2.0 binário) sem three.js: cabeçalho, sequência de chunks e os limites que o
 * JSON declara sobre o chunk binário. Assim um arquivo cortado ou inconsistente não passa como
 * válido; o conteúdo (cena, skin, clipes) é conferido por `validateEldrunGltf`.
 */

const GLB_MAGIC = 0x46546c67; // "glTF"
const CHUNK_JSON = 0x4e4f534a; // "JSON"
const CHUNK_BIN = 0x004e4942; // "BIN\0"

/**
 * O `TextDecoder` do ambiente (navegador, Node e Workers têm): o content e os pacotes que o importam
 * (protocol, sim, worldgen) compilam só com o lib ES2023, que não o declara.
 */
const utf8 = (): { decode(bytes: Uint8Array): string } =>
  new (globalThis as unknown as { TextDecoder: new (label: "utf-8", options: { fatal: boolean }) => { decode(bytes: Uint8Array): string } })
    .TextDecoder("utf-8", { fatal: true });

export interface Glb {
  /** O chunk JSON, parseado. */
  json: unknown;
  /** O chunk binário, se houver. */
  bin: Uint8Array | undefined;
}

/**
 * Lê um GLB 2.0: cabeçalho, chunk JSON primeiro, no máximo um chunk binário, e chunks que terminam
 * exatamente no fim do arquivo (chunks de outro tipo são ignorados, como manda a especificação).
 * Lança `Error` com o motivo em pt-BR.
 */
export function readGlb(bytes: Uint8Array): Glb {
  if (bytes.byteLength < 20) throw new Error("O arquivo não é um GLB: curto demais.");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0, true) !== GLB_MAGIC) throw new Error('O arquivo não é um GLB: falta a assinatura "glTF".');
  if (view.getUint32(4, true) !== 2) throw new Error("O GLB precisa ser glTF 2.0.");
  if (view.getUint32(8, true) !== bytes.byteLength) throw new Error("O GLB está incompleto: o tamanho no cabeçalho não bate com o arquivo.");

  let json: Uint8Array | undefined;
  let bin: Uint8Array | undefined;
  for (let offset = 12; offset < bytes.byteLength;) {
    if (offset + 8 > bytes.byteLength) throw new Error("O GLB está cortado: sobra um cabeçalho de chunk incompleto.");
    const start = offset + 8;
    const end = start + view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    if (end > bytes.byteLength) throw new Error("O GLB está cortado: um chunk passa do fim do arquivo.");
    if ((offset === 12) !== (type === CHUNK_JSON)) throw new Error("O GLB precisa de um chunk JSON, e só como primeiro chunk.");
    if (type === CHUNK_JSON) json = bytes.subarray(start, end);
    if (type === CHUNK_BIN) {
      if (bin !== undefined) throw new Error("O GLB tem mais de um chunk binário.");
      bin = bytes.subarray(start, end);
    }
    offset = end;
  }
  if (json === undefined) throw new Error("O GLB precisa de um chunk JSON, e só como primeiro chunk.");
  try {
    return { json: JSON.parse(utf8().decode(json)) as unknown, bin };
  } catch {
    throw new Error("O chunk JSON do GLB não é um JSON válido.");
  }
}

/** O chunk JSON de um GLB 2.0, parseado (`readGlb`). Lança `Error` com o motivo em pt-BR. */
export function readGlbJson(bytes: Uint8Array): unknown {
  return readGlb(bytes).json;
}

const COMPONENT_BYTES: Readonly<Record<number, number>> = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 };
const TYPE_COMPONENTS: Readonly<Record<string, number>> = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT2: 4, MAT3: 9, MAT4: 16 };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Inteiro ≥ 0 seguro, ou undefined. */
const size = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : undefined;

/** Uma bufferView utilizável; null = inválida (o problema já foi registrado). */
type View = { length: number; stride: number | undefined } | null;

/**
 * Confere o que o JSON diz do chunk binário: o GLB é autocontido (só o `buffers[0]`, sem `uri`, no
 * chunk binário), as bufferViews cabem no buffer e os accessors (inclusive os esparsos) cabem nas
 * bufferViews. É o que o `GLTFLoader` lê; passar do fim vira `RangeError` lá. Vazio = íntegro.
 */
export function glbBufferProblems(json: unknown, binLength: number | undefined): string[] {
  if (!isRecord(json)) return [];
  const problems: string[] = [];
  const list = (key: string): unknown[] => {
    const value = json[key];
    return Array.isArray(value) ? value : [];
  };

  const buffers = list("buffers").map((buffer, index): number | null => {
    const byteLength = isRecord(buffer) ? size(buffer["byteLength"]) : undefined;
    if (byteLength === undefined) {
      problems.push(`buffers[${index}].byteLength é inválido.`);
      return null;
    }
    if (isRecord(buffer) && buffer["uri"] !== undefined) {
      problems.push(`buffers[${index}] aponta para fora do arquivo (uri): o GLB precisa ser autocontido.`);
      return null;
    }
    if (index > 0) {
      problems.push(`buffers[${index}] não tem uri e não é o primeiro: só o buffers[0] fica no chunk binário.`);
      return null;
    }
    if (byteLength > (binLength ?? 0)) {
      problems.push(`buffers[0] tem ${byteLength} bytes, mas o chunk binário tem ${binLength ?? 0}.`);
      return null;
    }
    return byteLength;
  });

  const views = list("bufferViews").map((bufferView, index): View => {
    const record = isRecord(bufferView) ? bufferView : {};
    const buffer = size(record["buffer"]);
    const offset = size(record["byteOffset"] ?? 0);
    const length = size(record["byteLength"]);
    const stride = record["byteStride"] === undefined ? undefined : size(record["byteStride"]);
    if (buffer === undefined || offset === undefined || length === undefined || (record["byteStride"] !== undefined && stride === undefined)) {
      problems.push(`bufferViews[${index}] tem buffer, posição ou tamanho inválido.`);
      return null;
    }
    const available = buffers[buffer];
    if (available === undefined) problems.push(`bufferViews[${index}] usa o buffer ${buffer}, que não existe.`);
    else if (available !== null && offset + length > available) problems.push(`bufferViews[${index}] passa do fim do buffer ${buffer}.`);
    return available === undefined || available === null || offset + length > available ? null : { length, stride };
  });

  /** Confere que `needed` bytes cabem na bufferView; `label` nomeia quem lê. */
  const fits = (viewIndex: unknown, offset: unknown, element: number, count: number, label: string, strided: boolean): void => {
    const index = size(viewIndex);
    const start = size(offset ?? 0);
    if (index === undefined || start === undefined || element === 0) {
      problems.push(`${label} tem bufferView, posição ou tipo inválido.`);
      return;
    }
    const view = views[index];
    if (view === undefined) problems.push(`${label} usa a bufferView ${index}, que não existe.`);
    if (view === undefined || view === null || count === 0) return;
    const step = strided ? (view.stride ?? element) : element;
    if (start + step * (count - 1) + element > view.length) problems.push(`${label} passa do fim da bufferView ${index}.`);
  };

  list("accessors").forEach((accessor, index) => {
    if (!isRecord(accessor)) return;
    const count = size(accessor["count"]);
    const element = (COMPONENT_BYTES[accessor["componentType"] as number] ?? 0) * (TYPE_COMPONENTS[accessor["type"] as string] ?? 0);
    if (count === undefined || element === 0) {
      problems.push(`accessors[${index}] tem count, componentType ou type inválido.`);
      return;
    }
    if (accessor["bufferView"] !== undefined) fits(accessor["bufferView"], accessor["byteOffset"], element, count, `accessors[${index}]`, true);
    const sparse = accessor["sparse"];
    if (!isRecord(sparse)) return;
    const sparseCount = size(sparse["count"]) ?? 0;
    const indices = isRecord(sparse["indices"]) ? sparse["indices"] : {};
    const values = isRecord(sparse["values"]) ? sparse["values"] : {};
    fits(indices["bufferView"], indices["byteOffset"], COMPONENT_BYTES[indices["componentType"] as number] ?? 0, sparseCount, `accessors[${index}].sparse.indices`, false);
    fits(values["bufferView"], values["byteOffset"], element, sparseCount, `accessors[${index}].sparse.values`, false);
  });
  return problems;
}
